import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, ClipboardCheck, Check, X, Sparkles, User, CircleDot,
  ShieldCheck, Hourglass, FileCheck2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const APPROVAL_LABEL = { pending: "Väntar på beslut", approved: "Godkänt", rejected: "Nekat" };
const EXECUTION_LABEL = {
  not_started: "Ej utfört", auto_executed: "Utfört av systemet",
  manual_pending: "Väntar på manuellt utförande", done: "Utfört", failed: "Misslyckades",
};
const EVIDENCE_LABEL = { none: "—", pending: "Bevis saknas", verified: "Bevisat" };

function StatusChip({ tone, icon: Icon, children }) {
  const tones = {
    gold: "bg-[#D4B483]/12 text-[#D4B483] border-[#D4B483]/30",
    green: "bg-[#10B981]/12 text-[#10B981] border-[#10B981]/30",
    red: "bg-[#EF4444]/12 text-[#FCA5A5] border-[#EF4444]/30",
    gray: "bg-[#27272A]/60 text-[#C0C0C6] border-[#3F3F46]",
    blue: "bg-[#3B82F6]/12 text-[#93C5FD] border-[#3B82F6]/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${tones[tone] || tones.gray}`}>
      {Icon && <Icon className="w-3 h-3" />}{children}
    </span>
  );
}

export default function ProposalsTab({ isAdmin, onChange }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [rejectDialog, setRejectDialog] = useState(null);   // proposal
  const [rejectReason, setRejectReason] = useState("");
  const [completeDialog, setCompleteDialog] = useState(null); // proposal
  const [evidenceNote, setEvidenceNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/proposals");
      setProposals(res.data.proposals || []);
      onChange?.(res.data.pending || 0);
    } catch { toast.error("Kunde inte ladda förslag"); }
    finally { setLoading(false); }
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, fn) => {
    setBusyId(id);
    try { await fn(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Misslyckades"); }
    finally { setBusyId(null); }
  };

  const approve = (p) => act(p.id, async () => {
    const res = await api.post(`/admin/proposals/${p.id}/approve`);
    const ex = res.data.execution_status;
    toast.success(ex === "auto_executed" ? "Godkänt och utfört ✓"
      : ex === "failed" ? "Godkänt — men utförandet misslyckades"
      : "Godkänt — väntar på manuellt utförande + bevis");
  });

  const reject = () => {
    const p = rejectDialog;
    setRejectDialog(null);
    act(p.id, async () => {
      await api.post(`/admin/proposals/${p.id}/reject`, { reason: rejectReason.trim() });
      setRejectReason("");
      toast.success("Förslaget nekades");
    });
  };

  const complete = () => {
    const p = completeDialog;
    if (evidenceNote.trim().length < 5) { toast.error("Beskriv beviset (minst några ord)"); return; }
    setCompleteDialog(null);
    act(p.id, async () => {
      await api.post(`/admin/proposals/${p.id}/complete`, { evidence_note: evidenceNote.trim() });
      setEvidenceNote("");
      toast.success("Uppgift stängd med bevis ✓");
    });
  };

  const fmt = (s) => { try { return new Date(s).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } };

  const shown = proposals.filter(p =>
    filter === "all" ? true
    : filter === "pending" ? p.approval_status === "pending" || p.execution_status === "manual_pending"
    : p.approval_status === filter);
  const pendingCount = proposals.filter(p => p.approval_status === "pending").length;

  return (
    <div className="max-w-3xl" data-testid="proposals-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Förslag</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">AI:n och systemet föreslår — inget utförs utan ditt godkännande.</p>
        </div>
        <div className="flex gap-1.5">
          {[["pending", "Att hantera"], ["approved", "Godkända"], ["rejected", "Nekade"], ["all", "Alla"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${filter === v ? "bg-[#D4B483]/15 text-[#D4B483] border-[#D4B483]/40" : "text-[#C0C0C6] border-[#27272A] hover:border-[#3F3F46]"}`}>
              {l}{v === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <ClipboardCheck className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga förslag här. Be AI-rådgivaren om en ändring så dyker den upp som förslag.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map(p => (
            <div key={p.id} className={`bg-[#121212] rounded-xl border p-4 ${p.approval_status === "pending" ? "border-[#D4B483]/40" : "border-[#27272A]"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${p.source === "ai" ? "bg-[#D4B483]/15" : "bg-[#3B82F6]/15"}`}>
                  {p.source === "ai" ? <Sparkles className="w-4 h-4 text-[#D4B483]" /> : <User className="w-4 h-4 text-[#93C5FD]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">{p.title}</p>
                  {p.description && <p className="text-xs text-[#C8C8CE] mt-1 leading-relaxed whitespace-pre-wrap">{p.description}</p>}
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <StatusChip tone={p.approval_status === "pending" ? "gold" : p.approval_status === "approved" ? "green" : "red"} icon={ShieldCheck}>
                      {APPROVAL_LABEL[p.approval_status]}
                    </StatusChip>
                    <StatusChip tone={p.execution_status === "auto_executed" || p.execution_status === "done" ? "green" : p.execution_status === "failed" ? "red" : p.execution_status === "manual_pending" ? "blue" : "gray"} icon={CircleDot}>
                      {EXECUTION_LABEL[p.execution_status]}
                    </StatusChip>
                    <StatusChip tone={p.evidence_status === "verified" ? "green" : p.evidence_status === "pending" ? "blue" : "gray"} icon={FileCheck2}>
                      {EVIDENCE_LABEL[p.evidence_status]}
                    </StatusChip>
                    {p.confidence != null && <StatusChip tone="gray">säkerhet {Math.round(p.confidence)}%</StatusChip>}
                  </div>
                  <p className="text-[11px] text-[#9A9AA2] mt-2">
                    {p.source === "ai" ? "AI-förslag" : `Föreslaget av ${p.proposed_by}`} · {fmt(p.created_at)}
                    {p.approved_by && ` · godkänt av ${p.approved_by}`}
                    {p.rejected_by && ` · nekat av ${p.rejected_by}${p.rejection_reason ? ` (${p.rejection_reason})` : ""}`}
                  </p>
                  {p.result_summary && (
                    <p className={`text-xs mt-1.5 ${p.execution_status === "failed" ? "text-[#FCA5A5]" : "text-[#10B981]"}`}>{p.result_summary}</p>
                  )}
                  {(p.completion_evidence || []).filter(e => e.type !== "system_execution").map((e, i) => (
                    <p key={i} className="text-[11px] text-[#C9EFD9] mt-1 bg-[#10B981]/8 border border-[#10B981]/20 rounded px-2 py-1">
                      Bevis: {e.note} <span className="text-[#9A9AA2]">({e.added_by}, {fmt(e.added_at)})</span>
                    </p>
                  ))}
                </div>
                {isAdmin && p.approval_status === "pending" && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" onClick={() => approve(p)} disabled={busyId === p.id}
                      className="bg-[#10B981] hover:bg-[#059669] text-white h-8 text-xs">
                      {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Godkänn</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectDialog(p)} disabled={busyId === p.id}
                      className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8 text-xs">
                      <X className="w-3.5 h-3.5 mr-1" /> Neka
                    </Button>
                  </div>
                )}
                {isAdmin && p.execution_status === "manual_pending" && (
                  <Button size="sm" onClick={() => setCompleteDialog(p)} disabled={busyId === p.id}
                    className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-8 text-xs shrink-0">
                    <Hourglass className="w-3.5 h-3.5 mr-1" /> Klart — lägg bevis
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => { if (!o) setRejectDialog(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-white">Neka förslaget?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#D8D8DC]">{rejectDialog?.title}</p>
          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Anledning (valfritt)" rows={2}
            className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="border-[#3F3F46] text-white hover:bg-[#1A1A1A]" onClick={() => setRejectDialog(null)}>Avbryt</Button>
            <Button className="bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={reject}>Neka</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete-with-evidence dialog */}
      <Dialog open={!!completeDialog} onOpenChange={(o) => { if (!o) setCompleteDialog(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-white">Stäng uppgiften med bevis</DialogTitle></DialogHeader>
          <p className="text-sm text-[#D8D8DC]">{completeDialog?.title}</p>
          <p className="text-xs text-[#C0C0C6]">En uppgift räknas inte som klar för att någon säger det — beskriv beviset (t.ex. fakturanummer, banktransaktion, skickat mejl).</p>
          <Textarea value={evidenceNote} onChange={e => setEvidenceNote(e.target.value)}
            placeholder="T.ex: Betald, banköverföring 2026-06-29, ref 12345" rows={3}
            className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="border-[#3F3F46] text-white hover:bg-[#1A1A1A]" onClick={() => setCompleteDialog(null)}>Avbryt</Button>
            <Button className="bg-[#10B981] hover:bg-[#059669] text-white" onClick={complete}>Stäng med bevis</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
